import React from 'react'
import { Paper, Box, Typography, Stack } from '@mui/material'

/**
 * Carte de contenu standard : fond légèrement plus clair que le fond d'écran,
 * bordure discrète, coins arrondis. Sert de base à tous les blocs de contenu
 * pour éviter les `bgcolor="#ffffff" borderRadius={10}` dispersés.
 */
export const Surface = ({ children, sx, padding = 2, tone = 'default', ...rest }) => {
  const tones = {
    default: { bgcolor: 'background.paper', borderColor: 'divider' },
    success: { bgcolor: 'rgba(18,183,106,0.10)', borderColor: 'rgba(18,183,106,0.35)' },
    error: { bgcolor: 'rgba(240,68,56,0.10)', borderColor: 'rgba(240,68,56,0.35)' },
    warning: { bgcolor: 'rgba(247,144,9,0.10)', borderColor: 'rgba(247,144,9,0.35)' },
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderRadius: '18px',
        p: padding,
        ...tones[tone],
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Paper>
  )
}

/** Titre de section : petite étiquette en capitales + titre. */
export const SectionTitle = ({ overline, title, description, align = 'left', sx }) => (
  <Box sx={{ textAlign: align, ...sx }}>
    {overline ? (
      <Typography variant="overline" sx={{ color: 'primary.main', display: 'block' }}>
        {overline}
      </Typography>
    ) : null}
    {title ? (
      <Typography variant="h4" sx={{ mt: overline ? 0.5 : 0 }}>
        {title}
      </Typography>
    ) : null}
    {description ? (
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
        {description}
      </Typography>
    ) : null}
  </Box>
)

/**
 * Ligne libellé / valeur. Remplace les tableaux à 2 colonnes, qui se
 * compriment mal sur les écrans étroits : ici la valeur passe sous le
 * libellé dès que la place manque.
 */
export const InfoRow = ({ label, value, emphasis = false }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    justifyContent="space-between"
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    spacing={{ xs: 0.25, sm: 2 }}
    sx={{
      py: 1.5,
      '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
    }}
  >
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', flexShrink: 0, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography
      variant={emphasis ? 'h6' : 'body1'}
      sx={{
        fontWeight: emphasis ? 800 : 600,
        textAlign: { xs: 'left', sm: 'right' },
        minWidth: 0,
      }}
    >
      {value ?? '—'}
    </Typography>
  </Stack>
)

export default Surface
